/**
 * Assigned post-its board: incomplete (to assign) vs complete (assigned).
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, CalendarClock, Users } from 'lucide-react';
import { PostItCard } from '@/components/post-its/PostItCard';
import type { AssignedFilter } from '@/components/post-its/post-it-configs';
import {
  buildAssignedFooterBadge,
  buildAssignedPostItsQueryArgs,
  buildRolesIdToLabelMap,
  getPostItHeight,
  isAssignedTemplateComplete,
  sortAssignedTemplates,
  splitAssignedTemplates,
  toCardShape,
} from '@/components/post-its/post-it-table-utils';
import { TrackingDrawer } from './tracking/TrackingDrawer';
import { useQuery } from '@tanstack/react-query';
import { getAssignedPostIts } from '@/services/post-its.service';
import { fetchRoles } from '@/services/roles.service';
import { useMode } from '@/contexts/ModeContext';
import type { AssignedPostIt } from '@/types/post-it.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';

/** Props for {@link PostItsPlanningBoard}. */
interface PostItsPlanningBoardProps {
  filter: AssignedFilter;
  endDateFrom?: string;
  endDateTo?: string;
  quarterlyPeriods?: number[];
  className?: string;
  selectedIds?: string[];
  onToggleSelect?: (id: string) => void;
  onEditAssigned?: (t: AssignedPostIt) => void;
  onDeleteTemplate?: (id: string) => void;
  /** Fires after data loads — gives the parent the list of template IDs for select-all */
  onDataReady?: (ids: string[]) => void;
  sectionWidth?: ElementWidth;
}

/** Assigned post-its board: incomplete (to assign) vs complete (assigned). */
export const PostItsPlanningBoard: React.FC<PostItsPlanningBoardProps> = ({
  filter,
  endDateFrom,
  endDateTo,
  quarterlyPeriods,
  className,
  selectedIds = [],
  onToggleSelect,
  onEditAssigned,
  onDeleteTemplate,
  onDataReady,
  sectionWidth = 'full',
}) => {
  const postItHeight = getPostItHeight(sectionWidth);
  const [trackingDrawerOpen, setTrackingDrawerOpen] = React.useState(false);
  const [trackingTemplate, setTrackingTemplate] = React.useState<AssignedPostIt | null>(null);
  const [assignedOpen, setAssignedOpen] = React.useState(true);

  const { elements: dashboardElements } = useInterfaceElements('dashboard');
  /** Dashboard IE: open tracking drawer from a template card. */
  const canTrack = dashboardElements.some((el) => el.elementId === 'postits-assigned-tracking');

  const { mode: appMode } = useMode();

  /** Open the tracking drawer for one assigned template. */
  const handleOpenTracking = (template: AssignedPostIt) => {
    setTrackingTemplate(template);
    setTrackingDrawerOpen(true);
  };

  /** Close tracking and drop the selected template. */
  const handleCloseTracking = () => {
    setTrackingDrawerOpen(false);
    setTrackingTemplate(null);
  };

  const { data: rawTemplates = [], isLoading } = useQuery({
    queryKey: ['post-its', 'assigned', appMode, filter, endDateFrom, endDateTo, quarterlyPeriods],
    queryFn: () =>
      getAssignedPostIts(
        buildAssignedPostItsQueryArgs({
          filter,
          endDateFrom,
          endDateTo,
          quarterlyPeriods,
          mode: appMode ?? undefined,
        }),
      ),
    enabled: !!appMode,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['roles', 'all'],
    queryFn: fetchRoles,
  });

  const rolesMap = React.useMemo(() => buildRolesIdToLabelMap(roles), [roles]);

  const templates = React.useMemo(() => sortAssignedTemplates(rawTemplates), [rawTemplates]);

  React.useEffect(() => {
    if (!isLoading) {
      onDataReady?.(templates.filter((t) => !isAssignedTemplateComplete(t)).map((t) => t.id));
    }
  }, [templates, isLoading, onDataReady]);

  React.useEffect(() => {
    if (filter !== 'all') {
      setAssignedOpen(true);
    }
  }, [filter]);

  const gridStyle: React.CSSProperties = {
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  };

  const isSelectable = !!onToggleSelect;

  const { pending, assigned } = splitAssignedTemplates(templates);

  if (isLoading) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground animate-pulse">
        Chargement des post-its assignés…
      </div>
    );
  }

  return (
    <div className={cn('space-y-8 pr-2', className)}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[var(--color-2-nuance-1)]">
          À assigner ({pending.length})
        </h3>

        {pending.length > 0 ? (
          <div className="grid gap-4" style={gridStyle}>
            {pending.map((template) => (
              <PostItCard
                key={template.id}
                postIt={toCardShape(template)}
                permission="read"
                height={postItHeight}
                isSelectable={isSelectable}
                isSelected={selectedIds.includes(template.id)}
                hasActiveSelection={isSelectable}
                onSelect={() => onToggleSelect?.(template.id)}
                onEdit={onEditAssigned ? () => onEditAssigned(template) : undefined}
                onDelete={onDeleteTemplate ? () => onDeleteTemplate(template.id) : undefined}
                footerBadge={buildAssignedFooterBadge(template, rolesMap)}
              />
            ))}
          </div>
        ) : (
          <div className="table-empty">
            <Users className="table-empty-icon" />
            <p className="table-empty-text">Aucun post-it à configurer</p>
          </div>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setAssignedOpen((o) => !o)}
          className="w-full cursor-pointer text-base font-medium text-[var(--color-1-nuance-1)] mb-3 hover:text-[var(--color-2-nuance-1)] transition-colors flex items-center gap-2 px-3 py-3 rounded-lg border"
          style={{
            borderColor: 'color-mix(in srgb, var(--color-1-nuance-2) 40%, transparent)',
          }}
        >
          <ChevronDown
            className={cn('w-4 h-4 transition-transform', assignedOpen && 'rotate-180')}
          />
          <span>Assignés ({assigned.length})</span>
        </button>

        {assignedOpen &&
          (assigned.length > 0 ? (
            <div className="mt-4 grid gap-4" style={gridStyle}>
              {assigned.map((template) => {
                const isSelectableForAssigned =
                  !!onToggleSelect && isAssignedTemplateComplete(template);
                return (
                  <PostItCard
                    key={template.id}
                    postIt={toCardShape(template)}
                    permission="read"
                    height={postItHeight}
                    isSelectable={isSelectableForAssigned}
                    isSelected={selectedIds.includes(template.id)}
                    hasActiveSelection={isSelectableForAssigned}
                    onSelect={
                      isSelectableForAssigned ? () => onToggleSelect?.(template.id) : undefined
                    }
                    onEdit={onEditAssigned ? () => onEditAssigned(template) : undefined}
                    onTrack={canTrack ? () => handleOpenTracking(template) : undefined}
                    footerBadge={buildAssignedFooterBadge(template, rolesMap)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="mt-4 table-empty">
              <CalendarClock className="table-empty-icon" />
              <p className="table-empty-text">Aucun post-it configuré.</p>
            </div>
          ))}
      </div>

      {trackingTemplate && (
        <TrackingDrawer
          isOpen={trackingDrawerOpen}
          onClose={handleCloseTracking}
          templateId={trackingTemplate.id}
          templateTitle={trackingTemplate.title}
        />
      )}
    </div>
  );
};
